import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsData {
  totalUsers: number;
  monthlyActiveUsers: number;
  totalApplications: number;
  monthlyApplications: number;
  submittedApplications: number;
  conversionRate: number;
  totalQuotes: number;
  acceptedQuotes: number;
  avgResponseTime: number;
  totalBrokers: number;
  totalPeoProviders: number;
  applicationsByStatus: Array<{ status: string; count: number }>;
  userGrowth: {
    current: number;
    previous: number;
    percentage: number;
  };
  applicationGrowth: {
    current: number;
    previous: number;
    percentage: number;
  };
}

export const useAdminAnalytics = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        
        // Get date ranges
        const now = new Date();
        const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

        // Fetch all data in parallel
        const [
          usersResult,
          currentMonthUsersResult,
          previousMonthUsersResult,
          applicationsResult,
          currentMonthAppsResult,
          previousMonthAppsResult,
          quotesResult,
          brokersResult,
          peoProvidersResult,
          applicationsByStatusResult,
        ] = await Promise.all([
          // Total users
          supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true }),
          
          // Current month active users (created this month)
          supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', currentMonthStart.toISOString()),
          
          // Previous month users
          supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', previousMonthStart.toISOString())
            .lt('created_at', currentMonthStart.toISOString()),

          // Total applications
          supabase
            .from('applications')
            .select('id', { count: 'exact', head: true }),

          // Current month applications
          supabase
            .from('applications')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', currentMonthStart.toISOString()),

          // Previous month applications
          supabase
            .from('applications')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', previousMonthStart.toISOString())
            .lt('created_at', currentMonthStart.toISOString()),

          // Quotes data
          supabase
            .from('peo_quotes')
            .select('id, status, created_at'),

          // Brokers count
          supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
            .eq('is_broker', true),

          // PEO providers count
          supabase
            .from('peo_providers')
            .select('id', { count: 'exact', head: true }),

          // Applications by status
          supabase
            .from('applications')
            .select('status')
        ]);

        // Calculate metrics
        const totalUsers = usersResult.count || 0;
        const monthlyActiveUsers = currentMonthUsersResult.count || 0;
        const previousMonthUsers = previousMonthUsersResult.count || 0;
        
        const totalApplications = applicationsResult.count || 0;
        const monthlyApplications = currentMonthAppsResult.count || 0;
        const previousMonthApps = previousMonthAppsResult.count || 0;

        const quotes = quotesResult.data || [];
        const totalQuotes = quotes.length;
        const acceptedQuotes = quotes.filter(q => q.status === 'accepted').length;
        
        // Calculate conversion rate (submitted applications / total applications)
        const submittedApplications = applicationsByStatusResult.data?.filter(
          app => app.status === 'submitted' || app.status === 'completed'
        ).length || 0;
        const conversionRate = totalApplications > 0 ? (submittedApplications / totalApplications) * 100 : 0;

        // Calculate average response time (mock for now - would need message timestamps)
        const avgResponseTime = quotes.length > 0 ? 
          quotes.reduce((acc, quote) => {
            const hoursAgo = Math.floor((Date.now() - new Date(quote.created_at).getTime()) / (1000 * 60 * 60));
            return acc + Math.min(hoursAgo, 48); // Cap at 48 hours for realistic averages
          }, 0) / quotes.length : 0;

        // Applications by status
        const statusCounts = applicationsByStatusResult.data?.reduce((acc: Record<string, number>, app) => {
          acc[app.status] = (acc[app.status] || 0) + 1;
          return acc;
        }, {}) || {};

        const applicationsByStatus = Object.entries(statusCounts).map(([status, count]) => ({
          status,
          count: count as number
        }));

        // Calculate growth percentages
        const userGrowthPercentage = previousMonthUsers > 0 ? 
          ((monthlyActiveUsers - previousMonthUsers) / previousMonthUsers) * 100 : 0;
        
        const appGrowthPercentage = previousMonthApps > 0 ? 
          ((monthlyApplications - previousMonthApps) / previousMonthApps) * 100 : 0;

        const analyticsData: AnalyticsData = {
          totalUsers,
          monthlyActiveUsers,
          totalApplications,
          monthlyApplications,
          submittedApplications,
          conversionRate: Math.round(conversionRate * 10) / 10,
          totalQuotes,
          acceptedQuotes,
          avgResponseTime: Math.round(avgResponseTime * 10) / 10,
          totalBrokers: brokersResult.count || 0,
          totalPeoProviders: peoProvidersResult.count || 0,
          applicationsByStatus,
          userGrowth: {
            current: monthlyActiveUsers,
            previous: previousMonthUsers,
            percentage: Math.round(userGrowthPercentage * 10) / 10
          },
          applicationGrowth: {
            current: monthlyApplications,
            previous: previousMonthApps,
            percentage: Math.round(appGrowthPercentage * 10) / 10
          }
        };

        setAnalytics(analyticsData);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  return { analytics, isLoading, error };
};